(() => {
  'use strict';
  if (customElements.get('ix-order-review')) return;
  class IndexesOrderReview extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      this.labels = JSON.parse(this.querySelector('[data-review-labels]').textContent);
      this.link = this.querySelector('[data-review-contact]');
      this.root = this.dataset.root || '/';
      const on = (node, event, fn) => node.addEventListener(event, fn, { signal: this.events.signal });
      on(this.link, 'click', event => {
        event.preventDefault();
        if (!this.busy) this.continueToWhatsApp();
      });
      on(this.querySelector('[data-review-retry]'), 'click', () => this.refresh());
      on(document, 'indexes:cart-refreshed', () => this.refresh());
      on(window, 'pageshow', event => { if (event.persisted) this.refresh(); });
      on(document, 'visibilitychange', () => { if (!document.hidden) this.refresh(); });
      this.refresh();
    }
    disconnectedCallback() {
      this.events?.abort(); this.events = null; this.request?.abort();
    }
    say(message, error = false) {
      const status = this.querySelector('[data-review-status]');
      status.textContent = message; status.hidden = !message;
      this.querySelector('[data-review-retry]').hidden = !error;
    }
    setBusy(value) {
      this.busy = value;
      this.setAttribute('aria-busy', String(value));
      this.link.setAttribute('aria-disabled', String(value || this.failed || !this.cart?.items.length));
      this.querySelector('[data-review-retry]').disabled = value;
    }
    money(cents, currency = this.cart.currency) {
      return new Intl.NumberFormat(document.documentElement.lang || 'ar', {
        style: 'currency', currency, maximumFractionDigits: 2
      }).format(cents / 100);
    }
    unknown(item) { return item.original_line_price === 0 && item.final_line_price === 0; }
    safeLink(path) {
      const url = new URL(path, location.origin);
      if (url.origin !== location.origin || !url.pathname.startsWith(this.root + 'products/')) throw new Error('product URL');
      const canonical = new URL(url.pathname, this.dataset.storeUrl || location.origin);
      const variant = url.searchParams.get('variant');
      if (variant && /^\d{1,20}$/.test(variant)) canonical.searchParams.set('variant', variant);
      return canonical.href;
    }
    validate(cart) {
      if (!cart || !Array.isArray(cart.items) || !/^[A-Z]{3}$/.test(cart.currency) ||
          !Number.isSafeInteger(cart.total_price) || cart.total_price < 0) throw new Error('cart');
      for (const item of cart.items) {
        if (!Number.isSafeInteger(item.quantity) || item.quantity < 1 ||
            !Number.isSafeInteger(item.final_line_price) || item.final_line_price < 0 ||
            !Number.isSafeInteger(item.original_line_price) || item.original_line_price < 0 ||
            !Number.isSafeInteger(item.final_price) || item.final_price < 0 ||
            typeof item.product_title !== 'string') throw new Error('line');
        this.safeLink(item.url);
      }
      return cart;
    }
    summary(cart) {
      const unknown = cart.items.some(item => this.unknown(item));
      return {
        label: unknown ? this.labels.partial : this.labels.subtotal,
        amount: cart.items.some(item => !this.unknown(item)) ? this.money(cart.total_price, cart.currency) : this.labels.unknown,
        unknown
      };
    }
    message(cart) {
      const lines = [this.labels.greeting];
      for (const item of cart.items) {
        lines.push('', item.product_title);
        if (item.variant_title && item.variant_title !== 'Default Title') lines.push(item.variant_title);
        // Cart properties may contain private customer input: keep them off external URLs.
        if (Object.keys(item.properties || {}).some(key => !key.startsWith('_') && item.properties[key])) lines.push(this.labels.options);
        if (item.selling_plan_allocation) lines.push(this.labels.subscription);
        lines.push(this.labels.quantity + ': ' + item.quantity,
          this.labels.unit + ': ' + (this.unknown(item) ? this.labels.unknown : this.money(item.final_price, cart.currency)),
          this.labels.line_total + ': ' + (this.unknown(item) ? this.labels.unknown : this.money(item.final_line_price, cart.currency)),
          this.safeLink(item.url));
      }
      const summary = this.summary(cart);
      lines.push('', summary.label + ': ' + summary.amount);
      if (summary.unknown) lines.push(this.labels.unknown_note);
      lines.push(this.labels.message_note);
      return lines.join('\n');
    }
    node(tag, className, text) {
      const node = document.createElement(tag); if (className) node.className = className;
      if (text != null) node.textContent = text; return node;
    }
    render(cart) {
      this.cart = cart;
      const list = this.querySelector('[data-review-items]'); list.replaceChildren();
      for (const item of cart.items) {
        const row = this.node('article', 'ix-review__item');
        if (item.image) {
          try {
            const url = new URL(item.image, location.origin);
            if (url.protocol === 'https:' && (url.origin === location.origin || url.hostname === 'cdn.shopify.com')) {
              url.searchParams.set('width', '144');
              const image = this.node('img'); image.src = url.href; image.alt = ''; image.width = 64; image.height = 76; image.loading = 'lazy'; row.append(image);
            }
          } catch (_) { /* Product text remains available without a thumbnail. */ }
        }
        const detail = this.node('div', 'ix-review__detail');
        const title = this.node('a', '', item.product_title); title.href = this.safeLink(item.url); detail.append(title);
        if (item.variant_title && item.variant_title !== 'Default Title') detail.append(this.node('small', '', item.variant_title));
        for (const [key, value] of Object.entries(item.properties || {})) {
          if (!key.startsWith('_') && value) detail.append(this.node('small', '', key + ': ' + value));
        }
        if (item.selling_plan_allocation?.selling_plan?.name) detail.append(this.node('small', '', item.selling_plan_allocation.selling_plan.name));
        detail.append(this.node('small', '', this.labels.quantity + ': ' + item.quantity),
          this.node('strong', '', this.unknown(item) ? this.labels.unknown : this.money(item.final_line_price)));
        row.append(detail); list.append(row);
      }
      const summary = this.summary(cart);
      this.querySelector('[data-review-total-label]').textContent = summary.label;
      this.querySelector('[data-review-total]').textContent = summary.amount;
      this.querySelector('[data-review-unknown]').hidden = !summary.unknown;
      this.querySelector('[data-review-empty]').hidden = cart.items.length > 0;
      this.querySelector('[data-review-content]').hidden = cart.items.length === 0;
      if (!/^\d{8,15}$/.test(this.dataset.phone)) throw new Error('phone');
      this.link.href = 'https://wa.me/' + this.dataset.phone + '?text=' + encodeURIComponent(this.message(cart));
    }
    async refresh() {
      this.request?.abort();
      const request = this.request = new AbortController();
      const timeout = setTimeout(() => request.abort(), 12000);
      this.setBusy(true); this.say(this.labels.loading);
      try {
        const response = await fetch(this.root + 'cart.js', { cache: 'no-store', credentials: 'same-origin', signal: request.signal, headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('cart');
        const cart = this.validate(await response.json());
        if (!this.isConnected || request !== this.request) return null;
        this.render(cart); this.failed = false; this.say(''); return cart;
      } catch (_) {
        if (this.isConnected && request === this.request) { this.failed = true; this.say(this.labels.error, true); this.link.removeAttribute('href'); }
        return null;
      } finally {
        clearTimeout(timeout);
        if (request === this.request) this.setBusy(false);
      }
    }
    async continueToWhatsApp() {
      const previous = this.cart ? this.message(this.cart) : null;
      const cart = await this.refresh();
      if (!cart || !cart.items.length) return;
      if (previous !== this.message(cart)) { this.say(this.labels.changed); return; }
      // Same-tab navigation avoids asynchronous pop-up blocking. No order is created or cart cleared.
      location.assign(this.link.href);
    }
  }
  customElements.define('ix-order-review', IndexesOrderReview);
})();
